import React, {
  forwardRef,
  useEffect,
  useLayoutEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { createPortal } from 'react-dom';
import clsx from 'clsx';
import {
  ChevronDownIcon,
  MagnifyingGlassIcon,
  XMarkIcon,
} from '@heroicons/react/24/outline';

/** Values that render as muted placeholder text (e.g. "All statuses", value=""). */
const DEFAULT_PLACEHOLDER_VALUES = ['', 'all'];

export const SEARCHABLE_SELECT_TRIGGER_CLASS =
  'h-10 w-full flex items-center gap-1.5 px-3 border border-surface-200 rounded-lg bg-white text-sm text-left transition-all duration-200 focus:outline-none focus:border-brand-500 focus:ring-2 focus:ring-brand-500/10 hover:border-surface-300';

const PANEL_CLASS =
  'z-[9999] bg-white border border-surface-200 rounded-xl shadow-card-hover overflow-hidden';

/** No focus ring/border on in-panel search fields (avoids global :focus-visible styles). */
export const DROPDOWN_SEARCH_INPUT_CLASS =
  'dropdown-search-input flex-1 min-w-0 text-sm text-dark-900 placeholder:text-surface-400 bg-transparent';

function layoutClassName(className) {
  if (!className) return '';
  return className
    .split(/\s+/)
    .filter(
      (c) =>
        c &&
        (c.startsWith('w-') ||
          c.startsWith('min-w-') ||
          c.startsWith('max-w-') ||
          c === 'flex-1' ||
          c === 'shrink-0' ||
          c === 'grow' ||
          c === 'mt-1' ||
          c.startsWith('!')),
    )
    .join(' ');
}

function isPlaceholderValue(value, placeholderValues) {
  if (value == null) return true;
  return placeholderValues.some((v) => String(v) === String(value));
}

function collectOptions(children) {
  const collected = [];
  React.Children.forEach(children, (child) => {
    if (!child || child.type !== 'option') return;
    const childValue = child.props.value;
    const raw = child.props.children;
    let label;
    if (typeof raw === 'string' || typeof raw === 'number') {
      label = String(raw);
    } else if (Array.isArray(raw)) {
      label = raw
        .filter((c) => typeof c === 'string' || typeof c === 'number')
        .join('');
    } else {
      label = String(childValue ?? '');
    }
    collected.push({
      value: childValue,
      label: label.trim() || String(childValue ?? ''),
      disabled: !!child.props.disabled,
    });
  });
  return collected;
}

/**
 * SearchableSelect — single-select dropdown with search (API-compatible with <select>).
 */
const SearchableSelect = forwardRef(function SearchableSelect(
  {
    value,
    onChange,
    children,
    options,
    className = '',
    disabled = false,
    error = false,
    placeholder = 'Select…',
    searchPlaceholder = 'Search…',
    placeholderValues = DEFAULT_PLACEHOLDER_VALUES,
    panelMinWidth = 288,
    displayLabel: displayLabelOverride,
    name,
    id,
    required,
    /* Passed through to the trigger: a select with no visible label needs one for screen readers (and tests). */
    'aria-label': ariaLabel,
  },
  ref,
) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState('');
  const [coords, setCoords] = useState({ top: 0, left: 0, width: 0, placement: 'bottom' });
  const triggerRef = useRef(null);
  const panelRef = useRef(null);
  const searchInputRef = useRef(null);

  const setRefs = (node) => {
    triggerRef.current = node;
    if (typeof ref === 'function') ref(node);
    else if (ref) ref.current = node;
  };

  const normalizedPlaceholderValues = useMemo(
    () => (Array.isArray(placeholderValues) ? placeholderValues : DEFAULT_PLACEHOLDER_VALUES),
    [placeholderValues],
  );

  const items = useMemo(() => {
    if (Array.isArray(options)) {
      return options.map((o) => ({
        value: o.value,
        label: o.label ?? String(o.value),
        disabled: !!o.disabled,
      }));
    }
    return collectOptions(children);
  }, [options, children]);

  const selected = items.find((o) => String(o.value) === String(value));
  const showAsPlaceholder = isPlaceholderValue(value, normalizedPlaceholderValues);
  const triggerLabel = showAsPlaceholder
    ? selected?.label || placeholder
    : (displayLabelOverride ?? selected?.label);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return items;
    return items.filter((o) => o.label.toLowerCase().includes(q));
  }, [items, query]);

  const updateCoords = () => {
    if (!triggerRef.current) return;
    const r = triggerRef.current.getBoundingClientRect();
    const panelHeight = 280;
    const spaceBelow = window.innerHeight - r.bottom;
    const placement = spaceBelow < panelHeight && r.top > panelHeight ? 'top' : 'bottom';
    setCoords({
      top: placement === 'bottom' ? r.bottom + 4 : r.top - 4,
      left: r.left,
      width: r.width,
      placement,
    });
  };

  useLayoutEffect(() => {
    if (open) updateCoords();
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const frameId = requestAnimationFrame(() => searchInputRef.current?.focus());
    return () => cancelAnimationFrame(frameId);
  }, [open]);

  useEffect(() => {
    if (!open) return;
    const onScrollOrResize = () => updateCoords();
    const onDown = (e) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(e.target) &&
        panelRef.current &&
        !panelRef.current.contains(e.target)
      ) {
        setOpen(false);
        setQuery('');
      }
    };
    window.addEventListener('scroll', onScrollOrResize, true);
    window.addEventListener('resize', onScrollOrResize);
    document.addEventListener('mousedown', onDown);
    return () => {
      window.removeEventListener('scroll', onScrollOrResize, true);
      window.removeEventListener('resize', onScrollOrResize);
      document.removeEventListener('mousedown', onDown);
    };
  }, [open]);

  const selectValue = (val) => {
    onChange?.({ target: { value: val, name } });
    setOpen(false);
    setQuery('');
  };

  const widthClass = layoutClassName(className);

  return (
    <>
      <div className={widthClass || undefined}>
        <button
          ref={setRefs}
          type="button"
          id={id}
          disabled={disabled}
          aria-label={ariaLabel}
          aria-required={required || undefined}
          aria-haspopup="listbox"
          aria-expanded={open}
          onClick={() => !disabled && setOpen((o) => !o)}
          className={clsx(
            SEARCHABLE_SELECT_TRIGGER_CLASS,
            disabled && 'cursor-not-allowed opacity-60 bg-surface-50',
            !disabled && 'cursor-pointer',
            error && 'border-error-400 focus:border-error-500 focus:ring-error-500/10',
            className,
          )}
        >
          <span className="flex-1 min-w-0 truncate text-left">
            {showAsPlaceholder || !triggerLabel ? (
              <span className="text-surface-400 font-normal">
                {triggerLabel || placeholder}
              </span>
            ) : (
              <span className="text-dark-900">{triggerLabel}</span>
            )}
          </span>
          <ChevronDownIcon
            className={clsx(
              'w-3.5 h-3.5 text-surface-400 flex-shrink-0 transition-transform',
              open && 'rotate-180',
            )}
          />
        </button>
      </div>

      {open &&
        createPortal(
          <div
            ref={panelRef}
            style={{
              position: 'fixed',
              top: coords.top,
              left: coords.left,
              width: Math.max(coords.width, panelMinWidth),
              minWidth: panelMinWidth,
              transform: coords.placement === 'top' ? 'translateY(-100%)' : undefined,
            }}
            className={PANEL_CLASS}
            role="listbox"
          >
            <div className="flex items-center gap-2 px-3 py-2 border-b border-surface-100">
              <MagnifyingGlassIcon className="w-4 h-4 text-surface-400 flex-shrink-0" />
              <input
                ref={searchInputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder={searchPlaceholder}
                className={DROPDOWN_SEARCH_INPUT_CLASS}
              />
              {query && (
                <button
                  type="button"
                  onMouseDown={(e) => e.preventDefault()}
                  onClick={() => {
                    setQuery('');
                    searchInputRef.current?.focus();
                  }}
                  className="flex-shrink-0"
                  aria-label="Clear search"
                >
                  <XMarkIcon className="w-3.5 h-3.5 text-surface-400 hover:text-error-500" />
                </button>
              )}
            </div>
            <ul className="max-h-52 overflow-y-auto py-1">
              {filtered.length === 0 ? (
                <li className="px-4 py-2 text-sm text-surface-400">No results</li>
              ) : (
                filtered.map((o, i) => {
                  const isSelected = String(o.value) === String(value);
                  return (
                    <li key={`${o.value}-${i}`}>
                      <button
                        type="button"
                        disabled={o.disabled}
                        role="option"
                        aria-selected={isSelected}
                        onClick={() => !o.disabled && selectValue(o.value)}
                        className={clsx(
                          'w-full text-left px-4 py-2 text-sm transition-colors',
                          o.disabled
                            ? 'text-surface-300 cursor-not-allowed'
                            : isSelected
                              ? 'bg-brand-50 text-brand-700 font-medium'
                              : 'text-dark-800 hover:bg-surface-50',
                        )}
                      >
                        {o.label}
                      </button>
                    </li>
                  );
                })
              )}
            </ul>
          </div>,
          document.body,
        )}
    </>
  );
});

export default SearchableSelect;
